import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import TableComponent from '../../components/Table/TableComponent';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ConsultDetails, Docs, Health, PatientProfile, SummaryResponse, Vitals, History } from '../../components/interface/SummaryInterface';
import LoadingAnimation from '../../components/LoadingAnimation';
import { BASE_URL, getAxiosConfig } from '../../components/interface/Config';
import { apiHandler } from '../../services/authService';
// Component
const TeleconsultSummary: React.FC = () => {
  const [profile, setProfile] = useState<PatientProfile | null>(null);
  const [consultDetails, setConsultDetails] = useState<ConsultDetails | null>(null);
  const [mrn, setMrn] = useState<string | null>(null);
  const [age, setAge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // States for different types of fetched data
  const navigate = useNavigate();
  const [vitalsData, setVitalsData] = useState<Vitals | null>(null); // State for vitals data
  const [historyData, setHistoryData] = useState<History | null>(null); // State for history data
  const [healthData, setHealthData] = useState<Health | null>(null); // State for health data
  const [docsData, setDocsData] = useState<Docs | null>(null); // State for document data
  const getConsultNotesIconColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-500";
      case "Started":
        return "bg-[#ffae42]";
      case "Ended":
        return "bg-green-500";
      case "Cancelled":
        return "bg-red-500";
      case "Waiting":
        return "bg-yellow-500";
      default:
        return "bg-gray-300";
    }
  };
  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString: string) => {
    if (!dateString) return ''; // Check if dateString is provided
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return ''; // Check for invalid date

    const day = String(date.getDate()).padStart(2, '0'); // Ensures two-digit day
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensures two-digit month
    const year = date.getFullYear();

    return `${day}-${month}-${year}`; // Returns in dd-mm-yyyy format
  };
  const token = sessionStorage.getItem('authToken');
  const { publishertoken } = useParams<{ publishertoken: string }>();

  const fetchProfile = async () => {
    try {

      const url        = `${BASE_URL}/patient/summary?token=${publishertoken}&pdf_for=consult_report`;
      const response   = await apiHandler('', 'GET',{}, url);

      if (!response) {
          throw new Error("No response received from the server");
      }

      const data = response.data;

      console.log("Data:", data);
      
      // const response = await fetch(
      //   `${BASE_URL}/patient/summary?token=${publishertoken}&pdf_for=consult_report`,
      //   {
      //     method: "GET",
      //     headers: { ...getAxiosConfig().headers },

      //   }
      // );

      // if (!response.ok) throw new Error(`Failed to fetch profile data: ${await response.text()}`);

      // const data: SummaryResponse = await response.json();
      setProfile(data.data.profile.a_profile);
      setMrn(data.data.profile.b_patient.additional_info.mrn_number);
      setAge(data.data.profile.b_patient.additional_info.age);
      setConsultDetails(data.data.profile.b_consult_details);
      setVitalsData(data.data.vitals || null);
      setHealthData(data.data.health || null);
      setDocsData(data.data.docs || null);
      setHistoryData(data.data.history || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      console.error("Error fetching profile:", err);
    }
  };


  useEffect(() => {
    if (!publishertoken) {
      setError('Publisher token is missing.');
      return;
    }
    fetchProfile();
  }, [publishertoken, token]);

  if (!token) return <p>Error: Authorization token is missing. Please login again.</p>;
  if (error) return <p>Error: {error}</p>;
  if (!profile) return <div className="flex justify-center items-center h-screen bg-[#eaeaea]">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
  </div>;

  return (
    <div className="space-y-6 ">
      <div className="flex items-center justify-between mt-2 sticky top-0 z-10 bg-white shadow-md py-4 px-4">
        <button
          className="flex items-center justify-center w-9 h-9 bg-gray-200 text-black rounded-[8px] hover:bg-gray-400 transition duration-300 ease-in-out"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowBackIcon style={{ fontSize: "1rem", color: "black" }} />
        </button>
        <h2 className="text-xl font-semibold text-center flex-1">Patient and Consult Info</h2>
      </div>


      <div className="flex flex-col lg:flex-row bg-gray-100 p-4 border border-gray-300 shadow-md rounded-lg mt-4">
        <div className="flex-1 p-4 lg:border-r lg:border-gray-300">
          <div className="flex flex-wrap gap-4 text-sm text-gray-800">
            <div className="flex-1">
              <strong>Patient Details:</strong> {profile.first_name} {profile.last_name} | {profile.gender.charAt(0).toUpperCase()} | {age} Yrs
              {consultDetails?.reason && <div><strong>Reason:</strong> {consultDetails?.reason}</div>}
            </div>
            <div className="flex-1">
              {profile.mobile && <div><strong>Mobile:</strong> {profile.mobile}</div>}
              {profile.email && <div><strong>Email:</strong> {profile.email}</div>}
            </div>
          </div>
        </div>
        <div className="flex-1 p-4">
          {consultDetails ? (
            <div className="grid grid-cols-1 sm:grid-cols-2  text-sm text-gray-800">
              <div><strong>Scheduled at:</strong> {format(new Date(consultDetails.scheduled_at), 'dd-MM-yyyy hh:mm a')}</div>
              <div><strong>Created at:</strong> {format(new Date(consultDetails.created_at), 'dd-MM-yyyy hh:mm a')}</div>
              <div><strong>Started at:</strong> {format(new Date(consultDetails.started_at), 'dd-MM-yyyy hh:mm a')}</div>
              <div><strong>Ended at:</strong> {format(new Date(consultDetails.ended_at), 'dd-MM-yyyy hh:mm a')}</div>
            </div>
          ) : <p>Consult details are not available.</p>}
        </div>
        <div
          className={`flex items-center justify-center mt-4 h-8 text-white px-2 rounded-lg ${getConsultNotesIconColor(
            consultDetails?.consult_status?.name || ""
          )}`}
        >
          <p className="font-semibold">{consultDetails?.consult_status?.name}</p>
        </div>
      </div>

      <div className="overflow-y-auto p-6 max-h-[calc(100vh-250px)]">
        {/* BMI */}
        {vitalsData?.a_bmi && vitalsData.a_bmi.length > 0 && (
          <div>
            <p className='font-bold text-lg text-secondary flex items-center justify-center'>BMI</p>
            <TableComponent
              headers={['SN:no', 'Result', 'Height', 'Weight', 'BMI', 'Date']}
              data={vitalsData.a_bmi}
              renderRow={(item, index) => (
                <tr key={item.id}>
                  <td className="p-2">{index + 1}</td>
                  <td style={{ backgroundColor: item.details.range_code || 'transparent' }} className="m-2 p-2 flex items-center justify-center text-white border rounded-lg">
                    {item.details.bmiFlag || '-'}
                  </td>
                  <td className="p-2 max-w-xs truncate">{item.details.height || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.weight || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.bmi || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}
        {/* Temperature */}
        {vitalsData?.b_temperature && Array.isArray(vitalsData.b_temperature) && vitalsData.b_temperature.length > 0 && (
          <div className="">
            <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Temperature</p>
            <TableComponent
              headers={['S.No', 'Result', 'Temperature', 'Type', 'Date', 'Time']}
              data={vitalsData.b_temperature}
              renderRow={(item, index) => (
                <tr key={item.id} className="">
                  <td className="p-2">{index + 1}</td>
                  <td
                    style={{ backgroundColor: item.details.range_code || 'transparent' }}
                    className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                  >
                    {item.details.temperatureFlag || '-'}
                  </td>
                  <td className="p-2 max-w-xs truncate">{item.details.temperature || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.type || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}

        {vitalsData && vitalsData.d_spo2 && vitalsData.d_spo2.length > 0 && (
          <div className="">
            <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>SpO2</p>
            <TableComponent
              headers={['S.No', 'Result', 'Spo2', 'Date', 'Time']}
              data={vitalsData.d_spo2}
              renderRow={(item, index) => (
                <tr key={item.id} className="">
                  <td className="p-2">{index + 1}</td>
                  <td
                    style={{ backgroundColor: item.details.range_code || 'transparent' }}
                    className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                  >
                    {item.details.spo2Flag || '-'}
                  </td>
                  <td className="p-2 max-w-xs truncate">{item.details.spo2 || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}

        {vitalsData && vitalsData.k_hemoglobin && vitalsData.k_hemoglobin.length > 0 && (
          <div className="">
            <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Hemoglobin</p>
            <TableComponent
              headers={['S.No', 'Result', 'Hemoglobin', 'Date', 'Time']}
              data={vitalsData.k_hemoglobin}
              renderRow={(item, index) => (
                <tr key={item.id} className=" ">
                  <td className="p-2">{index + 1}</td>
                  <td
                    style={{ backgroundColor: item.details.range_code || 'transparent' }}
                    className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                  >
                    {item.details.hemoglobinFlag || '-'}
                  </td>
                  <td className="p-2 max-w-xs truncate">{item.details.hemoglobin || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}

        {vitalsData && vitalsData.c_bs && vitalsData.c_bs.length > 0 && (
          <div className="">
            <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>BloodSugar</p>
            <TableComponent
              headers={['S.No', 'Result', 'BloodSugar', 'Type', 'Date', 'Time']}
              data={vitalsData.c_bs}
              renderRow={(item, index) => (
                <tr key={item.id} className=" ">
                  <td className="p-2">{index + 1}</td>
                  <td
                    style={{ backgroundColor: item.details.range_code || 'transparent' }}
                    className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                  >
                    {item.details.bsFlagColor || '-'}
                  </td>
                  <td className="p-2 max-w-xs truncate">{item.details.blood_sugar || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.type || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                  <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}

        {vitalsData && vitalsData.e_urine && vitalsData.e_urine.length > 0 && (
          <div className="">
            <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Urine</p>
            <TableComponent
              headers={['S.No', 'Urine', 'Sugar', 'Leukocytes', 'Protein', 'Rbc', 'Date', 'Time']}
              data={vitalsData.e_urine}
              renderRow={(item, index) => (
                <tr key={item.id} className="">
                  <td className="p-2">{index + 1}</td>

                  {/* Urine value with dynamic background */}
                  <td className="p-2 max-w-xs truncate">
                    <span
                      style={{ backgroundColor: item?.details?.value_range_code || "transparent" }}
                      className="p-2 text-white text-sm border rounded-lg"
                    >
                      {item?.details?.value || "-"}
                    </span>
                    {item?.details?.urine || "-"}
                  </td>

                  {/* Sugar value with dynamic background */}
                  <td className="p-2 max-w-xs truncate">
                    <span
                      style={{ backgroundColor: item?.details?.sugar_range_code || "transparent" }}
                      className="p-2 text-white text-sm border rounded-lg"
                    >
                      {item?.details?.sugar_message || "-"}
                    </span>
                    {item?.details?.sugar || "-"}
                  </td>

                  {/* Leukocytes value with dynamic background */}
                  <td className="p-2 max-w-xs truncate">
                    <span
                      style={{ backgroundColor: item?.details?.leukocytes_range_code || "transparent" }}
                      className="p-2 text-white text-sm border rounded-lg"
                    >
                      {item?.details?.leukocytes_message || "-"}
                    </span>
                    {item?.details?.leukocytes || "-"}
                  </td>

                  {/* Protein value with dynamic background */}
                  <td className="p-2 max-w-xs truncate">
                    <span
                      style={{ backgroundColor: item?.details?.protein_range_code || "transparent" }}
                      className="p-2 text-white text-sm border rounded-lg"
                    >
                      {item?.details?.protein_message || "-"}
                    </span>
                    {item?.details?.protein || "-"}
                  </td>

                  {/* RBC value with dynamic background */}
                  <td className="p-2 max-w-xs truncate">
                    <span
                      style={{ backgroundColor: item?.details?.rbc_range_code || "transparent" }}
                      className="p-2 text-white text-sm border rounded-lg"
                    >
                      {item?.details?.rbc_message || "-"}
                    </span>
                    {item?.details?.rbc || "-"}
                  </td>

                  {/* Date */}
                  <td className="p-2 max-w-xs truncate">{item?.details?.date || "-"}</td>
                  <td className="p-2 max-w-xs truncate">{item?.details?.time || '-'}</td>
                </tr>
              )} error={''} />
          </div>
        )}

        <div className='overflow-y-scroll'>
          {vitalsData && vitalsData.f_bp && vitalsData.f_bp.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Blood Pressure</p>
              <TableComponent
                headers={['SN:no', 'Result', 'Systolic', 'Diastolic', 'Pulse', 'Date', 'Time']}
                data={vitalsData.f_bp}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{
                        backgroundColor: item.details.range_code || 'transparent',
                      }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.bpFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.systolic || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.diastolic || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.pulse || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.g_heart && vitalsData.g_heart.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Heart Rate</p>
              <TableComponent
                headers={['SN:no', 'Result', 'Heart', 'Date', 'Time']}
                data={vitalsData.g_heart}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{
                        backgroundColor: item.details.range_code || 'transparent',
                      }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.heartRateFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.heart || '-'} {item.details.unit || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.h_lipid && vitalsData.h_lipid.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Lipid Profile</p>
              <TableComponent
                headers={['SN:no', 'Lipid Profile', 'Total', 'Date', 'Time']}
                data={vitalsData.h_lipid}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">
                      {/* LDL value with background and message */}
                      <span
                        style={{ backgroundColor: item?.details?.ldl_range_code || "transparent" }}
                        className="p-2 text-white text-sm border rounded-lg"
                      >
                        {item?.details?.ldl_message || "-"}
                      </span>
                      <span className='font-semibold'>Ldl : </span> {item?.details?.ldl || "-"} <span className="ml-1">{item?.details?.ldl_unit || "-"}</span>
                      {/* Add other values below, like HDL, Triglycerides, etc. */}
                      <div className="mt-4">
                        <span style={{ backgroundColor: item?.details?.hdl_range_code || "transparent" }}
                          className="p-2 text-white text-sm border rounded-lg"
                        >
                          {item?.details?.hdl_message || "-"}</span>  <span className='font-semibold'>Hdl : </span>{item?.details?.hdl || "-"} <span>{item?.details?.hdl_unit || "-"}</span>
                      </div>
                      <div className="mt-4">
                        <span style={{ backgroundColor: item?.details?.vldl_range_code || "transparent" }}
                          className="p-2 text-white text-sm border rounded-lg"
                        >
                          {item?.details?.vldl_message || "-"}</span>  <span className='font-semibold'>Vldl : </span>{item?.details?.triglycerides || "-"} <span>{item?.details?.triglycerides_unit || "-"}</span>
                      </div>
                      <div className="mt-4">
                        <span style={{ backgroundColor: item?.details?.hdl_ldl_range_code || "transparent" }}
                          className="p-2 text-white text-sm border rounded-lg"
                        >
                          {item?.details?.hdl_ldl_message || "-"}</span>  <span className='font-semibold'>HDL / LDL : </span>{item?.details?.hdl_ldl || "-"} <span>{item?.details?.hdl_ldl_unit || "-"}</span>
                      </div>
                      <div className="mt-4">
                        <span style={{ backgroundColor: item?.details?.triglycerides_range_code || "transparent" }}
                          className="p-2 text-white text-sm border rounded-lg"
                        >
                          {item?.details?.triglycerides_message || "-"}</span>  <span className='font-semibold'>Triglycerides : </span>{item?.details?.triglycerides || "-"} <span>{item?.details?.hdl_ldl_unit || "-"}</span>
                      </div>
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.total || '-'} {item.details.total_unit || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.i_respiration && vitalsData.i_respiration.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Respiration</p>
              <TableComponent
                headers={['S.No', 'Result', 'Respiration', 'Date', 'Time']}
                data={vitalsData.i_respiration}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{ backgroundColor: item.details.range_code || 'transparent' }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.respirationFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.respiration || '-'} Per Minute</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.j_hct && vitalsData.j_hct.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>HCT</p>
              <TableComponent
                headers={['S.No', 'Result', 'HCT', 'Date', 'Time']}
                data={vitalsData.j_hct}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{ backgroundColor: item.details.range_code || 'transparent' }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.hctFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.hct || '-'} %</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.l_keytone && vitalsData.l_keytone.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Keytone</p>
              <TableComponent
                headers={['S.No', 'Result', 'Keytone', 'Date', 'Time']}
                data={vitalsData.l_keytone}
                renderRow={(item, index) => (
                  <tr key={item.id} className="hover:bg-gray-100">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{ backgroundColor: item.details.range_code || 'transparent' }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.keytoneFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.keytone || '-'} mmol/L</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {vitalsData && vitalsData.m_uric_acid && vitalsData.m_uric_acid.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex items-center text-secondary justify-center mt-6'>Uric Acid</p>
              <TableComponent
                headers={['S.No', 'Result', 'Uric Acid', 'Date', 'Time']}
                data={vitalsData.m_uric_acid}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td
                      style={{ backgroundColor: item.details.range_code || 'transparent' }}
                      className="m-2 p-2 flex items-center justify-center text-white border rounded-lg"
                    >
                      {item.details.uricFlag || '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.details.uric_acid || '-'} mg/dL</td>
                    <td className="p-2 max-w-xs truncate">{item.details.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.details.time || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}


          {/* HISTORY */}
          {historyData && historyData.a_medical_history && historyData.a_medical_history.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center'>Medical History</p>
              <TableComponent
                headers={['S.No', 'Condition', 'OnSet Date', 'Recovered Date', 'Treatment', 'Status']}
                data={historyData.a_medical_history}
                renderRow={(item, index) => (
                  <tr key={item.values.condition.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.condition.slug || '-'}</td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.onset_date ? new Date(item.values.onset_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.recovered_on ? new Date(item.values.recovered_on).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.values.treatment || '-'}</td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.is_active == '1' ? 'Active' : 'Inactive'}
                    </td>
                  </tr>
                )} error={''} />
            </div>
          )}


          {historyData && historyData.b_surgical_history && historyData.b_surgical_history.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Surgical History</p>
              <TableComponent
                headers={['S.No', 'Surgical', 'Due To', 'Notes', 'Status', 'Prescribed', 'Done By', 'Date']}
                data={historyData.b_surgical_history}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.surgical_name?.name || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.pre_surgery_diagnosis?.name || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.surgery_notes || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.surgery_status || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.prescribed_by || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.surgery_done_by || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.surgery_date || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}


          {historyData && historyData.c_social_history && historyData.c_social_history.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Social History</p>
              <TableComponent
                headers={['S.No', 'Details', 'Date', 'Remark']}
                data={historyData.c_social_history}
                renderRow={(item, index) => {
                  // Create a string to display only if the values are true
                  const socialHistoryStatus = [
                    item.values.tobacco && "Tobacco",
                    item.values.nontobacco && "NonTobacco",
                    item.values.alcohol && "Alcohol"
                  ].filter(Boolean).join(", ");

                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">
                        {socialHistoryStatus || '-'}
                      </td>
                      <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{item.values.notes || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}


          {historyData && historyData.h_diagnosis && historyData.h_diagnosis.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Diagnosis</p>
              <TableComponent
                headers={['S.No', 'Condition', 'Remarks', 'Date']}
                data={historyData.h_diagnosis}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.condition.slug || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.remarks || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}
          {/* HEALTH */}
          {healthData && healthData.a_chief_complaints && healthData.a_chief_complaints.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Chief Complaints</p>
              <TableComponent
                headers={['S.No', 'Title', 'Notes']}
                data={healthData.a_chief_complaints}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.addition_info?.title || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.addition_info?.notes || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.d_hpi && healthData.d_hpi.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>HPI</p>
              <TableComponent
                headers={['S.No', 'Signs/Symptoms', 'Location', 'Quality/Type', 'Severity', 'Duration & Timing', 'Date', 'Context', 'Relieved', 'Worsened', 'Free Text']}
                data={healthData.d_hpi}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.signs || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.location || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.quality || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.severity || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.timing || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.context || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.relieved || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.worsened || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.free_text || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.a_allergy && healthData.a_allergy.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Allergy</p>
              <TableComponent
                headers={['S.No', 'Allergy', 'Type', 'Category', 'Reaction', 'Other Reaction', 'Severity', 'Onset', 'Treated By', 'Notes', 'Status']}
                data={healthData.a_allergy}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.name.slug || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.type || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.category || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.reaction.slug || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.other_reaction || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.severity || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.treated_by || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.treatment || '-'}</td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.name.is_active == '1' ? 'Active' : 'Inactive'}
                    </td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.b_medicine && healthData.b_medicine.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Medicine</p>
              <TableComponent
                headers={['S.No', 'Medicine', 'Type', 'Strength', 'InTake Days', 'Quantity', 'Sig Info', 'Start', 'End', 'Reason', 'Status']}
                data={healthData.b_medicine}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.name.name || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.type || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.strength || '-'} {item.values.strength_measurement}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.intake_total_days || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.quantity || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.sig || '-'}</td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.start_date ? new Date(item.values.start_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.end_date ? new Date(item.values.end_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-2 max-w-xs truncate">{item.values.reason || '-'}</td>
                    <td className="p-2 max-w-xs truncate">
                      {item.values.is_active == '1' ? 'Active' : 'Inactive'}
                    </td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.c_diet && healthData.c_diet.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Diet</p>
              <TableComponent
                headers={['S.No', 'Date', 'Time', 'Category', 'Type', 'Food', 'In-take']}
                data={healthData.c_diet}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.time || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.category || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.type || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.food || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.intake || '-'} - {item.values.unit || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.e_procedure && healthData.e_procedure.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Procudure</p>
              <TableComponent
                headers={['S.No', 'Date', 'Procedure', 'Code', 'Primary Provider', 'Secondary Provider', 'Notes']}
                data={healthData.e_procedure}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.name || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.code || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.provider_one || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.provider_two || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.notes || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.g_surgical_procedure && healthData.g_surgical_procedure.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Surgical Procedures</p>
              <TableComponent
                headers={['S.No', 'Procedure', 'Date', 'Notes']}
                data={healthData.g_surgical_procedure}
                renderRow={(item, index) => (
                  <tr key={item.id} className="">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.name || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.date || '-'}</td>
                    <td className="p-2 max-w-xs truncate">{item.values.notes || '-'}</td>
                  </tr>
                )} error={''} />
            </div>
          )}

          {healthData && healthData.h_prescription && healthData.h_prescription.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Prescription</p>
              <TableComponent
                headers={['S.No', 'Name', 'Qty', 'Type', 'Medicine Taken', 'Medicine Instruction', 'Duration']}
                data={healthData.h_prescription}
                renderRow={(item, index) => {
                  // Ensure values is an array before accessing its properties
                  const prescriptionValue = Array.isArray(item.values) && item.values.length > 0 ? item.values[0] : {};

                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.medicine_name || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.total_qty || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.medicine_type || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.medicine_takenat || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.medicine_instruction || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{prescriptionValue.medicine_duration_type || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}


          {healthData && healthData.h_examination && healthData.h_examination.length > 0 &&
            healthData.h_examination.some(item => item.attributes?.folio_values && Array.isArray(item.attributes.folio_values) && item.attributes.folio_values.length > 0) && (
              <div className="mt-6">
                <p className="font-bold text-lg text-secondary text-center mb-6">Examination</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {healthData.h_examination.map((item) => { // Removed index if not needed
                    // Ensure item.attributes.folio_values exists and is an array with length > 0
                    if (item.attributes?.folio_values && Array.isArray(item.attributes.folio_values) && item.attributes.folio_values.length > 0) {
                      return (
                        <div
                          key={item.id}
                          className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{item.name || '-'}</span>
                            <span className="text-sm text-gray-500"></span>
                          </div>
                          <div className="mt-2">
                            <div className="flex flex-wrap mt-1">
                              {item.attributes.folio_values.map((value, valueIndex) => (
                                <span
                                  key={valueIndex}
                                  className="bg-blue-200 text-blue-800 text-sm font-semibold mr-2 mb-2 px-3 py-1 rounded-full"
                                >
                                  {value || '-'}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null; // Return null if folio_values are not present
                  })}
                </div>
              </div>
            )}


          {docsData && docsData.a_lab && docsData.a_lab.length > 0 && (
            <div className="">
              <p className='font-bold text-lg text-secondary flex items-center justify-center mt-6'>Lab</p>
              <TableComponent
                headers={['S.No', 'LabTest', 'Date', 'Notes']}
                data={docsData.a_lab}
                renderRow={(item, index) => {
                  // Safely access the date and provide a fallback if it's undefined
                  const formattedDate = item.addition_info?.date ? formatDate(item.addition_info.date) : '-'; // Use custom date formatting
                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.title || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{formattedDate}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.notes || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}

          {docsData && docsData.b_imaging && docsData.b_imaging.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Imaging</p>
              <TableComponent
                headers={['S.No', 'Scan Type', 'Date', 'Notes']}
                data={docsData.b_imaging}
                renderRow={(item, index) => {
                  // Safely access the date and provide a fallback if it's undefined
                  const formattedDate = item.addition_info?.date ? formatDate(item.addition_info.date) : '-'; // Use custom date formatting

                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.title || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{formattedDate}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.notes || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}

          {docsData && docsData.e_notes && docsData.e_notes.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>Notes</p>
              <TableComponent
                headers={['S.No', 'Procedure', 'Notes']}
                data={docsData.e_notes}
                renderRow={(item, index) => {
                  // Safely access the date and provide a fallback if it's undefined
                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.title || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.notes || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}

          {docsData && docsData.c_icd && docsData.c_icd.length > 0 && (
            <div className="">
              <p className='font-bold text-lg flex text-secondary items-center justify-center mt-6'>ICD</p>
              <TableComponent
                headers={['S.No', 'Procedure', 'Notes']}
                data={docsData.c_icd}
                renderRow={(item, index) => {
                  // Safely access the date and provide a fallback if it's undefined
                  return (
                    <tr key={item.id} className="">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.title || '-'}</td>
                      <td className="p-2 max-w-xs truncate">{item.addition_info?.notes || '-'}</td>
                    </tr>
                  );
                }} error={''} />
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default TeleconsultSummary;